import * as Yup from 'yup';
import { StageType } from "../../../../../graphql/types";
import { interventionValidationResourseUsagePartV2 } from './interventionValidationSchemaGeneralPart';
import {
    interventionFieldWithAtLeastMin,
    interventionFieldWithMin,
    interventionFieldWithMinMax
} from "./interventionValidationObjectBuilderFunctions";
import {
    numericOptionalWithGreaterThanMin,
    numericOptionalWithMin,
    numericRequiredWithGreaterThanMin,
    numericRequiredWithMin,
    numericRequiredWithMinMax
} from "../../Baseline/validationSchema/validationObjectBuilderFunctions";
import {
    availableMmsTypeForMonthlyStoragePeriods,
    availableMmsTypeForOverOrUnderStoragePeriod
} from "../../../../sustell_15/models/Intervention/DairyV2Intervention";
import { IntlShape } from "react-intl";

const isValid = (value: any) => value !== undefined && value !== null && value !== '' && !isNaN(Number(value));

const feedRequiredSumValidation = (intl: IntlShape, fieldName: string, customId: string = '') =>
    interventionFieldWithAtLeastMin(intl, fieldName, 0).test(
        'feedRequiredSum',
        '',
        function (_, context) {
            const { path, createError } = this;
            const { from }: any = context;

            if (from && from[2].value) {
                const { feed } = from[2].value;

                if (feed) {
                    const {
                        compoundFeeds, compoundFeedsAdditions,
                        singleIngredients, singleIngredientsAdditions,
                        freshGrass, freshGrassAdditions,
                        baselineCompoundFeedTotal,
                        baselineSingleIngredientTotal,
                        baselineFreshGrassTotal,
                    } = feed;

                    let feedSum = 0;

                    if (compoundFeeds && compoundFeeds.length > 0) {
                        compoundFeeds.forEach((item: any) => {
                            feedSum += isValid(item.kgPerAnimal_newValue)
                                ? Number(item.kgPerAnimal_newValue)
                                : Number(baselineCompoundFeedTotal);
                        });
                    } else {
                        feedSum += Number(baselineCompoundFeedTotal);
                    }

                    if (singleIngredients && singleIngredients.length > 0) {
                        singleIngredients.forEach((item: any) => {
                            feedSum += isValid(item.kgPerAnimal_newValue)
                                ? Number(item.kgPerAnimal_newValue)
                                : Number(baselineSingleIngredientTotal);
                        });
                    } else {
                        feedSum += Number(baselineSingleIngredientTotal);
                    }

                    if (freshGrass && freshGrass.length > 0) {
                        freshGrass.forEach((item: any) => {
                            feedSum += isValid(item.amount_newValue)
                                ? Number(item.amount_newValue)
                                : Number(baselineFreshGrassTotal);
                        });
                    } else {
                        feedSum += Number(baselineFreshGrassTotal);
                    }

                    // Additions
                    if (compoundFeedsAdditions) {
                        compoundFeedsAdditions.forEach((item: any) => {
                            if (isValid(item.kgPerAnimal)) {
                                feedSum += Number(item.kgPerAnimal);
                            }
                        });
                    }

                    if (singleIngredientsAdditions) {
                        singleIngredientsAdditions.forEach((item: any) => {
                            if (isValid(item.kgPerAnimal)) {
                                feedSum += Number(item.kgPerAnimal);
                            }
                        });
                    }

                    if (freshGrassAdditions) {
                        freshGrassAdditions.forEach((item: any) => {
                            if (isValid(item.amount)) {
                                feedSum += Number(item.amount);
                            }
                        });
                    }

                    if (feedSum <= 0.0) {
                        return createError({
                            path,
                            message: intl.formatMessage({
                                id: customId || 'SUSTELL.STAGE.DAIRY.FEED.SUM_OF_FEED.GREATER_THAN_ZERO',
                            }),
                        });
                    }

                    return true;
                }

                return false;
            }

            return false;
        }
    );

const housingTimeValidation = (intl: IntlShape, fieldName: string) =>
    interventionFieldWithMinMax(intl, fieldName, 0, 100).test(
        'housingTime',
        '',
        function (_, testContext) {
            const { path, createError } = this;
            const { from }: any = testContext;

            if (from && from[1].value) {
                const { housing } = from[1].value;

                if (housing) {
                    let timeSum = 0;

                    timeSum += isValid(housing.timeInHousing)
                        ? Number(housing.timeInHousing)
                        : Number(housing.baselineTimeInHousing);

                    timeSum += isValid(housing.timeInFeedlot)
                        ? Number(housing.timeInFeedlot)
                        : Number(housing.baselineTimeInFeedlot);

                    timeSum += isValid(housing.timeGrazingLargeAreas)
                        ? Number(housing.timeGrazingLargeAreas)
                        : Number(housing.baselineTimeGrazingLargeAreas);

                    timeSum += isValid(housing.timeGrazingPastures)
                        ? Number(housing.timeGrazingPastures)
                        : Number(housing.baselineTimeGrazingPastures);

                    if (timeSum !== 100) {
                        return createError({
                            path,
                            message: intl.formatMessage({
                                id: 'SUSTELL.STAGE.DAIRY.HOUSING.TIME_SUM_MUST_BE_100',
                            }),
                        });
                    }

                    return true;
                }

                return false;
            }

            return false;
        }
    );

const isStoragePeriodRequired = (selectedMmsType: any) =>
    availableMmsTypeForMonthlyStoragePeriods.some(
        (mmsType) => mmsType === selectedMmsType
    ) ||
    availableMmsTypeForOverOrUnderStoragePeriod.some(
        (mmsType) => mmsType === selectedMmsType
    );

const haveMoreThanOneMms = (from: any) => {
    if (from) {
        return ((from[1].value.manureSystems.length + from[1].value.manureSystemsAdditions.length) > 1);
    }

    return false;
};

const manureShareValidation = (intl: IntlShape) =>
    interventionFieldWithMinMax(intl, 'share', 0, 100).test(
        'manureShare',
        '',
        function (_, testContext) {
            const { path, createError } = this;
            const { from }: any = testContext;

            if (from && from[1]) {
                const { manureSystems, manureSystemsAdditions } = from[1].value;

                let shareSum = 0;

                if (manureSystems) {
                    manureSystems.forEach((item: any) => {
                        shareSum += isValid(Number(item.share)) ? Number(item.share) : 0;
                    });
                }

                if (manureSystemsAdditions) {
                    manureSystemsAdditions.forEach((item: any) => {
                        shareSum += isValid(Number(item.share)) ? Number(item.share) : 0;
                    });
                }

                if (shareSum !== 100) {
                    return createError({
                        path,
                        message: intl.formatMessage({
                            id: 'SUSTELL.STAGE.DAIRY.HOUSING.SHARE_SUM_MUST_BE_100',
                        }),
                    });
                }

                return true;
            }

            return false;
        }
    );

const internalSourcesDairyCowValidation = (intl: IntlShape) =>
    Yup.array().of(
        Yup.object({
            originStageId: Yup.string(),
            numberAnimals: interventionFieldWithMin(intl, 'numberAnimals', 0),
            shrinkingRate: interventionFieldWithMinMax(intl, 'shrinkingRate', 0, 100)
        }).default({})
    );

const internalSourcesContinuousGrowingValidation = (intl: IntlShape) =>
    Yup.array().of(
        Yup.object({
            originStageId: Yup.string(),
            animalType: Yup.string()
                .when('stageType', {
                    is: (value: StageType) => value === StageType.DairyCow,
                    then: Yup.string()
                }),
            numberAnimals: interventionFieldWithMin(intl, 'numberAnimals', 0),
            shrinkingRate: interventionFieldWithMinMax(intl, 'shrinkingRate', 0, 100)
        }).default({})
    );

const externalSourcesValidation = (intl: IntlShape) =>
    Yup.array().of(
        Yup.object({
            numberAnimals: interventionFieldWithMin(intl, 'numberAnimals', 0),
            shrinkingRate: interventionFieldWithMinMax(intl, 'shrinkingRate', 0, 100)
        }).default({})
    );

const stageInputDairyCow = (intl: IntlShape) =>
    Yup.object({
        animalsPresentAtStart: interventionFieldWithMin(intl, 'animalsPresentAtStart', 0),
        animalsPresentAtEnd: interventionFieldWithMin(intl, 'animalsPresentAtEnd', 0),
        averageWeightAtStart: Yup.number()
            .transform((changed, original) => {
                return original === '' ? undefined : changed;
            })
            .when('animalsPresentAtStart', {
                is: (value: number) => value > 0,
                then: interventionFieldWithMin(intl, 'averageWeightAtStart', 0)
            }).nullable(true),
        averageWeightNewAnimals: interventionFieldWithMin(intl, 'averageWeightNewAnimals', 0),

        internalSources: internalSourcesDairyCowValidation(intl),
        externalSources: externalSourcesValidation(intl),
    }).default({});

const stageInputContinuousGrowing = (intl: IntlShape) =>
    Yup.object({
        animalsPresentAtStart: interventionFieldWithMin(intl, 'animalsPresentAtStart', 0),
        animalsPresentAtEnd: interventionFieldWithMin(intl, 'animalsPresentAtEnd', 0),
        averageWeightAtStart: Yup.number()
            .transform((changed, original) => {
                return original === '' ? undefined : changed;
            })
            .when('animalsPresentAtStart', {
                is: (value: number) => value > 0,
                then: interventionFieldWithMin(intl, 'averageWeightAtStart', 0),
            }).nullable(true),
        averageWeightNewAnimals: interventionFieldWithMin(intl, 'averageWeightNewAnimals', 0),
        averageAge: interventionFieldWithMin(intl, 'averageAge', 0),

        internalSources: internalSourcesContinuousGrowingValidation(intl),
        externalSources: externalSourcesValidation(intl),
    },
    ).default({});

const stageFeedCompoundFeeds = (intl: IntlShape, isAddition: boolean) =>
    !isAddition ? (
        //intervention
        Yup.array().of(
            Yup.object({
                originalKgPerAnimal: feedRequiredSumValidation(intl, 'kgPerAnimal'),
                kgPerAnimal: feedRequiredSumValidation(intl, 'kgPerAnimal')
            }).default({}),
        )) : (
        //addition
        Yup.array().of(
            Yup.object({
                feedType: Yup.string(),
                kgPerAnimal: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .when('feedType', {
                        is: (value: any) => value,
                        then: numericRequiredWithMin(intl, 0),
                        otherwise: numericOptionalWithMin(intl, 0)
                    })
            }).default({}),
        ));


const stageFeedSingleIngredients = (intl: IntlShape, isAddition: boolean) =>
    !isAddition ? (
        //intervention
        Yup.array().of(
            Yup.object({
                originalKgPerAnimal: feedRequiredSumValidation(intl, 'kgPerAnimal'),
                kgPerAnimal: feedRequiredSumValidation(intl, 'kgPerAnimal'),

                origin: Yup.string()
                    .when('kgPerAnimal', {
                        is: (value: any) => value,
                        then: Yup.string().required(intl.formatMessage({ id: 'VALIDATION.FIELD.INPUT_SELECT' })),
                        otherwise: Yup.string(),
                    })
            }).default({})
        )) : (
        // addition
        Yup.array().of(
            Yup.object({
                feedType: Yup.string(),
                kgPerAnimal: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .when('feedType', {
                        is: (value: any) => value,
                        then: numericRequiredWithMin(intl, 0),
                        otherwise: numericOptionalWithMin(intl, 0)
                    }),
                origin: Yup.string()
                    .when('feedType', {
                        is: (value: any) => value,
                        then: Yup.string().required(intl.formatMessage({ id: 'VALIDATION.FIELD.INPUT_SELECT' })),
                        otherwise: Yup.string(),
                    })
            }).default({}),
        ));

const stageFeedFreshGrass = (intl: IntlShape, isAddition: boolean) =>
    !isAddition ? (
        // intervention
        Yup.array().of(
            Yup.object({
                originalAmount: feedRequiredSumValidation(intl, 'amount'),
                amount: feedRequiredSumValidation(intl, 'amount'),

                originalCrudeProteinContent: interventionFieldWithAtLeastMin(intl, 'crudeProteinContent', 0),
                crudeProteinContent: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .concat(interventionFieldWithAtLeastMin(intl, 'crudeProteinContent', 0)).nullable(true),

                originalDryMatterContent: interventionFieldWithAtLeastMin(intl, 'dryMatterContent', 0),
                dryMatterContent: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .concat(interventionFieldWithAtLeastMin(intl, 'dryMatterContent', 0)).nullable(true),

                originalDigestibility: interventionFieldWithAtLeastMin(intl, 'digestibility', 0),
                digestibility: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .concat(interventionFieldWithAtLeastMin(intl, 'digestibility', 0)).nullable(true),

                originalNeutralDetergentFiber: interventionFieldWithAtLeastMin(intl, 'neutralDetergentFiber', 0),
                neutralDetergentFiber: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .concat(interventionFieldWithAtLeastMin(intl, 'neutralDetergentFiber', 0)).nullable(true),
            }).default({})
        )) : (
        // addition
        Yup.array().of(
            Yup.object({
                type: Yup.string(),
                amount: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .when('type', {
                        is: (value: any) => !!value,
                        then: numericRequiredWithMin(intl, 0)
                    }),
                crudeProteinContent: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .when('type', {
                        is: (value: any) => !!value,
                        then: numericRequiredWithMin(intl, 0)
                    }),
                dryMatterContent: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .when('type', {
                        is: (value: any) => !!value,
                        then: numericRequiredWithMin(intl, 0)
                    }),
                digestibility: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .when('type', {
                        is: (value: any) => !!value,
                        then: numericRequiredWithMin(intl, 0)
                    }),
                neutralDetergentFiber: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .when('type', {
                        is: (value: any) => !!value,
                        then: numericRequiredWithMin(intl, 0)
                    }),
            }).default({}),
        )
    );

const stageFeedDairyCow = (intl: IntlShape) =>
    Yup.object({
        compoundFeeds: stageFeedCompoundFeeds(intl, false),
        singleIngredients: stageFeedSingleIngredients(intl, false),
        freshGrass: stageFeedFreshGrass(intl, false),
        compoundFeedsAdditions: stageFeedCompoundFeeds(intl, true),
        singleIngredientsAdditions: stageFeedSingleIngredients(intl, true),
        freshGrassAdditions: stageFeedFreshGrass(intl, true),
    }).default({});

const stageFeedContinuousGrowing = (intl: IntlShape) =>
    Yup.object({
        dietCharacterisation: Yup.string(),
        compoundFeeds: stageFeedCompoundFeeds(intl, false),
        singleIngredients: stageFeedSingleIngredients(intl, false),
        freshGrass: stageFeedFreshGrass(intl, false),
        compoundFeedsAdditions: stageFeedCompoundFeeds(intl, true),
        singleIngredientsAdditions: stageFeedSingleIngredients(intl, true),
        freshGrassAdditions: stageFeedFreshGrass(intl, true),
    }).default({});

const stageHousingMMS = (intl: IntlShape, isAddition: boolean) =>
    !isAddition ? (
        // intervention
        Yup.array().when('timeInHousing', {
            is: (value: any) => value,
            then: Yup.array()
                .of(
                    Yup.object({
                        storagePeriod: Yup.string(),

                        originalShare: interventionFieldWithMinMax(intl, 'share', 0, 100),
                        share: interventionFieldWithMinMax(intl, 'share', 0, 100),
                    }).default({})
                )
        })) : (
        // addition
        Yup.array().when('mmsType', {
            is: (value: any) => value,
            then: Yup.array().of(
                Yup.object({
                    mmsType: Yup.string().required(
                        intl.formatMessage({ id: 'VALIDATION.FIELD.INPUT_SELECT' })
                    ),
                    storagePeriod: Yup.string().when('mmsType', {
                        is: (val: string) => isStoragePeriodRequired(val),
                        then: Yup.string().required(
                            intl.formatMessage({ id: 'VALIDATION.FIELD.INPUT_SELECT' })
                        ),
                        otherwise: Yup.string(),
                    }),
                    share: Yup.string().when('mmsType', {
                        is: (val: string) => isStoragePeriodRequired(val),
                        then: numericRequiredWithMinMax(intl, 0, 100)
                    }),
                }).default({})
            )
        }));

const stageHousingBedding = (intl: IntlShape, isAddition: boolean) =>
    !isAddition ? (
        // intervention
        Yup.array().of(
            Yup.object({
                originalBeddingAmount: interventionFieldWithMin(intl, 'beddingAmount', 0),
                beddingAmount: interventionFieldWithMin(intl, 'beddingAmount', 0),
            }).default({})
        )) : (
        // addition
        Yup.array().of(
            Yup.object({
                beddingType: Yup.string(),
                beddingAmount: Yup.number()
                    .transform((changed, original) => {
                        return original === '' ? undefined : changed;
                    })
                    .when('beddingType', {
                        is: (val: any) => val,
                        then: numericRequiredWithGreaterThanMin(intl, 0),
                        otherwise: numericOptionalWithGreaterThanMin(intl, 0),
                    }),
            }).default({})
        ));

const stageHousingDairyCow = (intl: IntlShape) =>
    Yup.object({
        originalTimeInHousing: housingTimeValidation(intl, `timeInHousing`),
        originalTimeInFeedlot: housingTimeValidation(intl, `timeInFeedlot`),
        originalTimeGrazingLargeAreas: housingTimeValidation(intl, `timeGrazingLargeAreas`),
        originalTimeGrazingPastures: housingTimeValidation(intl, `timeGrazingPastures`),

        timeInHousing: housingTimeValidation(intl, `timeInHousing`),
        timeInFeedlot: housingTimeValidation(intl, `timeInFeedlot`),
        timeGrazingLargeAreas: housingTimeValidation(intl, `timeGrazingLargeAreas`),
        timeGrazingPastures: housingTimeValidation(intl, `timeGrazingPastures`),

        timeInHousingTied: Yup.number()
            .transform((changed, original) => {
                return original === '' ? undefined : changed;
            })
            .when('timeInHousing', {
                is: (value: number) => value > 0,
                then: interventionFieldWithMinMax(intl, 'timeInHousingTied', 0, 100),
            }).nullable(true),

        manureSystems: stageHousingMMS(intl, false),
        beddingSystems: stageHousingBedding(intl, false),
        manureSystemsAdditions: stageHousingMMS(intl, true),
        beddingSystemsAdditions: stageHousingBedding(intl, true),
    }).default({});

const stageHousingContinuousGrowing = (intl: IntlShape) =>
    Yup.object({
        originalTimeInHousing: housingTimeValidation(intl, `timeInHousing`),
        originalTimeInFeedlot: housingTimeValidation(intl, `timeInFeedlot`),
        originalTimeGrazingLargeAreas: housingTimeValidation(intl, `timeGrazingLargeAreas`),
        originalTimeGrazingPastures: housingTimeValidation(intl, `timeGrazingPastures`),

        timeInHousing: housingTimeValidation(intl, `timeInHousing`),
        timeInFeedlot: housingTimeValidation(intl, `timeInFeedlot`),
        timeGrazingLargeAreas: housingTimeValidation(intl, `timeGrazingLargeAreas`),
        timeGrazingPastures: housingTimeValidation(intl, `timeGrazingPastures`,),

        manureSystems: stageHousingMMS(intl, false),
        beddingSystems: stageHousingBedding(intl, false),
        manureSystemsAdditions: stageHousingMMS(intl, true),
        beddingSystemsAdditions: stageHousingBedding(intl, true),
    }).default({});

const stageOutputDairyCow = (intl: IntlShape) =>
    Yup.object({
        originalYearlyMilkProduction: interventionFieldWithMin(intl, 'yearlyMilkProduction', 0),
        originalAverageMilkFat: interventionFieldWithMinMax(intl, 'averageMilkFat', 0, 100),
        originalAverageMilkProtein: interventionFieldWithMinMax(intl, 'averageMilkProtein', 0, 100),
        originalCowsToAnotherStage: interventionFieldWithMin(intl, 'cowsToAnotherStage', 0),
        originalAverageWeightOfCowsLeavingTheStage: interventionFieldWithMin(intl, 'averageWeightOfCowsLeavingTheStage', 0),
        originalCowsSold: interventionFieldWithMin(intl, 'cowsSold'),
        originalNumberOfMortalitiesCows: interventionFieldWithMin(intl, 'numberOfMortalitiesCows', 0),
        originalAverageWeightAtMortalityCows: interventionFieldWithMin(intl, 'averageWeightAtMortalityCows', 0),
        originalAverageWeightAtBirth: interventionFieldWithMin(intl, 'averageWeightAtBirth', 0),
        originalWeanedCalvesToAnotherStage: interventionFieldWithMin(intl, 'weanedCalvesToAnotherStage', 0),
        originalWeanedCalvesSold: interventionFieldWithMin(intl, 'weanedCalvesSold', 0),

        yearlyMilkProduction: interventionFieldWithMin(intl, 'yearlyMilkProduction', 0),
        averageMilkFat: interventionFieldWithMinMax(intl, 'averageMilkFat', 0, 100),
        averageMilkProtein: interventionFieldWithMinMax(intl, 'averageMilkProtein', 0, 100),
        cowsToAnotherStage: interventionFieldWithMin(intl, 'cowsToAnotherStage', 0),
        averageWeightOfCowsLeavingTheStage: interventionFieldWithMin(intl, 'averageWeightOfCowsLeavingTheStage', 0),
        cowsSold: interventionFieldWithMin(intl, 'cowsSold'),
        numberOfMortalitiesCows: interventionFieldWithMin(intl, 'numberOfMortalitiesCows', 0),
        averageWeightAtMortalityCows: interventionFieldWithMin(intl, 'averageWeightAtMortalityCows', 0),
        averageWeightAtBirth: interventionFieldWithMin(intl, 'averageWeightAtBirth', 0),
        weanedCalvesToAnotherStage: interventionFieldWithMin(intl, 'weanedCalvesToAnotherStage', 0),
        weanedCalvesSold: interventionFieldWithMin(intl, 'weanedCalvesSold', 0),
    }).default({});

const stageOutputContinuousGrowing = (intl: IntlShape) =>
    Yup.object({
        originalCowsToAnotherStage: interventionFieldWithMin(intl, 'cowsToAnotherStage', 0),
        originalAverageWeightOfCowsLeavingTheStage: interventionFieldWithMin(intl, 'averageWeightOfCowsLeavingTheStage', 0),
        originalCowsSold: interventionFieldWithMin(intl, 'cowsSold', 0),
        originalAverageAge: interventionFieldWithMin(intl, 'averageAge', 0),
        originalNumberOfMortalitiesCows: interventionFieldWithMin(intl, 'numberOfMortalitiesCows', 0),
        originalAverageWeightAtMortalityCows: interventionFieldWithMin(intl, 'averageWeightAtMortalityCows', 0),

        cowsToAnotherStage: interventionFieldWithMin(intl, 'cowsToAnotherStage', 0),
        averageWeightOfCowsLeavingTheStage: interventionFieldWithMin(intl, 'averageWeightOfCowsLeavingTheStage', 0),
        cowsSold: interventionFieldWithMin(intl, 'cowsSold', 0),
        averageAge: interventionFieldWithMin(intl, 'averageAge', 0),
        numberOfMortalitiesCows: interventionFieldWithMin(intl, 'numberOfMortalitiesCows', 0),
        averageWeightAtMortalityCows: interventionFieldWithMin(intl, 'averageWeightAtMortalityCows', 0),
    }).default({});

const stageEmissions = (intl: IntlShape) =>
    Yup.object({
        emissionMitigations: Yup.object({
            originalMethaneEntericFermentation: interventionFieldWithMinMax(intl, 'methaneEntericFermentation', -100, 100),
            originalNmvoc: interventionFieldWithMinMax(intl, 'nmvoc', -100, 100),
            originalMethaneMms: interventionFieldWithMinMax(intl, 'methaneMms', -100, 100),
            originalNitrousOxideLeaching: interventionFieldWithMinMax(intl, 'nitrousOxideLeaching', -100, 100),
            originalNitrousOxideDirect: interventionFieldWithMinMax(intl, 'nitrousOxideDirect', -100, 100),
            originalNitrousOxideVolatilization: interventionFieldWithMinMax(intl, 'nitrousOxideVolatilization', -100, 100),
            originalNitrogenOxidesStorage: interventionFieldWithMinMax(intl, 'nitrogenOxidesStorage', -100, 100),
            originalAmmoniaStorage: interventionFieldWithMinMax(intl, 'ammoniaStorage', -100, 100),
            originalAmmoniaHousing: interventionFieldWithMinMax(intl, 'ammoniaHousing', -100, 100),
            originalAmmoniaYard: interventionFieldWithMinMax(intl, 'ammoniaYard', -100, 100),
            originalPm2_5: interventionFieldWithMinMax(intl, 'pm2_5', -100, 100),
            originalPm10: interventionFieldWithMinMax(intl, 'pm10', -100, 100),
            originalTsp: interventionFieldWithMinMax(intl, 'tsp', -100, 100),

            methaneEntericFermentation: interventionFieldWithMinMax(intl, 'methaneEntericFermentation', -100, 100),
            nmvoc: interventionFieldWithMinMax(intl, 'nmvoc', -100, 100),
            methaneMms: interventionFieldWithMinMax(intl, 'methaneMms', -100, 100),
            nitrousOxideLeaching: interventionFieldWithMinMax(intl, 'nitrousOxideLeaching', -100, 100),
            nitrousOxideDirect: interventionFieldWithMinMax(intl, 'nitrousOxideDirect', -100, 100),
            nitrousOxideVolatilization: interventionFieldWithMinMax(intl, 'nitrousOxideVolatilization', -100, 100),
            nitrogenOxidesStorage: interventionFieldWithMinMax(intl, 'nitrogenOxidesStorage', -100, 100),
            ammoniaStorage: interventionFieldWithMinMax(intl, 'ammoniaStorage', -100, 100),
            ammoniaHousing: interventionFieldWithMinMax(intl, 'ammoniaHousing', -100, 100),
            ammoniaYard: interventionFieldWithMinMax(intl, 'ammoniaYard', -100, 100),
            pm2_5: interventionFieldWithMinMax(intl, 'pm2_5', -100, 100),
            pm10: interventionFieldWithMinMax(intl, 'pm10', -100, 100),
            tsp: interventionFieldWithMinMax(intl, 'tsp', -100, 100),
        }).default({}),
    }).default({});

const stageDataPartDairyV2 = (confObj: { intl: IntlShape; userUOM?: any; }) =>
    Yup.object({
        stages: Yup.array().of(
            Yup.object({
                type: Yup.string().oneOf([
                    StageType.DairyCow,
                    StageType.ContinuousGrowing,
                ]),
                stageData: Yup.object()
                    .when('type', {
                        is: StageType.DairyCow,
                        then: Yup.object({
                            input: stageInputDairyCow(confObj.intl),
                            feed: stageFeedDairyCow(confObj.intl),
                            housing: stageHousingDairyCow(confObj.intl),
                            output: stageOutputDairyCow(confObj.intl),
                            emissions: stageEmissions(confObj.intl),
                        }).default({})
                    })
                    .when('type', {
                        is: StageType.ContinuousGrowing,
                        then: Yup.object({
                            input: stageInputContinuousGrowing(confObj.intl),
                            feed: stageFeedContinuousGrowing(confObj.intl),
                            housing: stageHousingContinuousGrowing(confObj.intl),
                            output: stageOutputContinuousGrowing(confObj.intl),
                            emissions: stageEmissions(confObj.intl),
                        }).default({}),
                    }),
            }).default({})
        ).required().min(1, confObj.intl.formatMessage({ id: 'SUSTELL.STAGE.MIN.REQUIRED' })),
    }).default({});

const assembleValidationSchemaSustellDairyV2 = (
    baseSchema: any,
    confObj: { intl: IntlShape; userUOM?: any; }
) => {
    const infoObject = baseSchema;
    const combinedSchema = Yup.object({ info: infoObject })
        .concat(Yup.object({ resourceUse: interventionValidationResourseUsagePartV2(confObj) }))
        .concat(stageDataPartDairyV2(confObj));

    return combinedSchema;
};

export default assembleValidationSchemaSustellDairyV2;